import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { doFetch, doPost, doDelete } from '../../../redux/api/actions';
import { GET_TOUR_SHARES, CREATE_TOUR_SHARE, DELETE_TOUR_SHARE } from '../../../redux/api/constants';
import './TourSharingModal.css';

function TourSharingModal({ tourId, onClose }) {
    const dispatch = useDispatch();
    const accessToken = useSelector(state => state.Auth.accessToken);
    const sharesData = useSelector(state => state.API.tourShares);

    const [userEmail, setUserEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [sharing, setSharing] = useState(false);

    useEffect(() => {
        if (tourId) {
            dispatch(doFetch(GET_TOUR_SHARES, `tourguide/tours/${tourId}/shares`, accessToken));
        }
    }, [tourId, accessToken, dispatch]);

    const handleShare = (e) => {
        e.preventDefault();

        // Validate email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(userEmail)) {
            setEmailError('Please enter a valid email address');
            return;
        }

        setSharing(true);
        setEmailError('');

        const shareData = { user_email: userEmail };

        dispatch(doPost(CREATE_TOUR_SHARE, `tourguide/tours/${tourId}/share`, accessToken, shareData));

        // Refresh shares list after a delay
        setTimeout(() => {
            dispatch(doFetch(GET_TOUR_SHARES, `tourguide/tours/${tourId}/shares`, accessToken));
            setUserEmail('');
            setSharing(false);
        }, 500);
    };

    const handleRemoveShare = (shareId) => {
        dispatch(doDelete(DELETE_TOUR_SHARE, `tourguide/tours/${tourId}/shares/${shareId}`, accessToken));

        // Refresh shares list
        setTimeout(() => {
            dispatch(doFetch(GET_TOUR_SHARES, `tourguide/tours/${tourId}/shares`, accessToken));
        }, 500);
    };

    const shares = sharesData?.shares || [];
    const totalShares = sharesData?.total || 0;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Manage Tour Sharing</h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="sharing-content">
                    <p className="sharing-info">
                        Share this private tour with specific users by entering their email address.
                        They'll be able to view and stream the tour once shared.
                    </p>

                    <form onSubmit={handleShare} className="share-form">
                        <div className="form-section">
                            <label className="form-label">User Email</label>
                            <div className="share-input-group">
                                <input
                                    type="email"
                                    className={`form-input ${emailError ? 'error' : ''}`}
                                    placeholder="user@example.com"
                                    value={userEmail}
                                    onChange={(e) => {
                                        setUserEmail(e.target.value);
                                        setEmailError('');
                                    }}
                                    disabled={sharing}
                                />
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={sharing || !userEmail}
                                >
                                    {sharing ? 'Sharing...' : 'Share'}
                                </button>
                            </div>
                            {emailError && <span className="error-text">{emailError}</span>}
                        </div>
                    </form>

                    <div className="shares-list">
                        <h4>Shared With ({totalShares})</h4>

                        {shares.length === 0 ? (
                            <p className="empty-state">
                                This tour hasn't been shared with anyone yet.
                            </p>
                        ) : (
                            <div className="shared-users">
                                {shares.map((share) => (
                                    <div key={share.id} className="shared-user-card">
                                        <div className="user-info">
                                            <span className="user-email">{share.user_email || share.user_id}</span>
                                            <span className="share-date">
                                                Shared {new Date(share.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <button
                                            className="btn-remove"
                                            onClick={() => handleRemoveShare(share.id)}
                                            title="Remove access"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="modal-actions">
                    <button className="btn-secondary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default TourSharingModal;
